<template>
  <ScreenHotkeyGroup class="mb-4" v-if="paymentRequired">
    <ScreenHotkeyBtn
      v-for="item in availablePaymentMethods"
      :key="item.value"
      :hotkey="item.hotkey"
      :text="item.title"
      :value="item.value"
      :id="item.id"
      @select="selectPaymentMethod"
      :active="item.value === paymentMethod"
      :payment="true"
    />
  </ScreenHotkeyGroup>
  <div class="billing-input-group">
    <TextField
      name="first_name"
      label="First Name"
      placeholder="First Name"
      :autoFocus="true"
      :isValidationAttempt="isSubmitAttempt"
      v-model="form.firstName"
      @on-focus="handleFocus"
      @update:modelValue="value => updateField('firstName', value)"
      required
    />
    <TextField
      name="last_name"
      label="Last Name"
      placeholder="Last Name"
      :isValidationAttempt="isSubmitAttempt"
      v-model="form.lastName"
      @on-focus="handleFocus"
      @update:modelValue="value => updateField('lastName', value)"
      required
    />
  </div>
  <slot />
  <div class="billing-input-group" v-if="paymentRequired">
    <TextField
      name="postcode"
      label="Zip/Postal Code"
      placeholder="Zip/Postal Code"
      :isValidationAttempt="isSubmitAttempt"
      v-model="form.zipCode"
      @on-focus="handleFocus"
      @update:modelValue="value => updateField('zipCode', value)"
      required
    />
    <SelectField
      name="country"
      placeholder="Country"
      label="Country"
      :options="countries"
      :isValidationAttempt="isSubmitAttempt"
      v-model="form.country"
      @update:modelValue="selectCountry"
      @on-focus="handleFocus"
      required
    />
  </div>
</template>
<script setup lang="ts">
import { ref } from 'vue'
import { storeToRefs } from 'pinia'
import { BillingForm, RecurlyBillingInputGroup } from '@/types'
import ScreenHotkeyGroup from '@/components/shared/screens/ScreenHotkeyGroup.vue'
import ScreenHotkeyBtn from '@/components/shared/screens/ScreenHotkeyBtn.vue'
import SelectField from '@/components/shared/inputs/SelectField.vue'
import TextField from '@/components/shared/inputs/TextField.vue'
import { useAddressStore } from '@/store/AddressStore'
import { useCheckoutStore } from '@/store/CheckoutStore'
import { useErrorsStore } from '@/store/ErrorsStore'
import { useRecurlyStore } from '@/store/RecurlyStore'

const addressStore = useAddressStore()
const checkoutStore = useCheckoutStore()
const errorsStore = useErrorsStore()
const recurlyStore = useRecurlyStore()

const { countries } = storeToRefs(addressStore)
const { setCheckoutCurrency, setCheckoutProperty } = checkoutStore
const { paymentMethod, availablePaymentMethods } = storeToRefs(recurlyStore)

const { clear: clearErrors } = errorsStore
const isSubmitAttempt = ref(false)
const handleFocus = () => clearErrors()

const props = defineProps<RecurlyBillingInputGroup>()
const emit = defineEmits(['update:form', 'selectPaymentMethod'])

const updateField = (key: keyof BillingForm, value: string) => {
  emit('update:form', { ...props.form, [key]: value })
}

const selectPaymentMethod = ({ value }: { value: string }) => {
  emit('selectPaymentMethod', value)
}

const selectCountry = (country: string) => {
  setCheckoutProperty({ country })
  setCheckoutCurrency(country)
}
</script>

<style lang="scss" scoped>
.billing-input-group {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
}
</style>
